import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchProducts, updateProductMachines, fetchMaterials } from '../store/slices/productsSlice';
import { fetchMachines } from '../store/slices/machinesSlice';

const Products = () => {
  const dispatch = useDispatch();
  const { products, pagination, loading, materials } = useSelector((state) => state.products);
  const { machines } = useSelector((state) => state.machines);
  const [filters, setFilters] = useState({ materialType: '', search: '', page: 1 });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showMachineModal, setShowMachineModal] = useState(false);
  const [selectedMachines, setSelectedMachines] = useState([]);
  const [primaryMachine, setPrimaryMachine] = useState('');

  useEffect(() => {
    dispatch(fetchMaterials());
    dispatch(fetchMachines());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchProducts(filters));
  }, [dispatch, filters]);

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value, page: 1 });
  };

  const openMachineModal = (product) => {
    setSelectedProduct(product);
    setSelectedMachines(product.compatibleMachines?.map(m => m._id || m) || []);
    setPrimaryMachine(product.primaryMachine?._id || product.primaryMachine || '');
    setShowMachineModal(true);
  };

  const handleSaveMachines = () => {
    if (selectedProduct) {
      dispatch(updateProductMachines({
        id: selectedProduct._id,
        machines: {
          primaryMachine: primaryMachine || null,
          compatibleMachines: selectedMachines
        }
      }));
      setShowMachineModal(false);
    }
  };

  const toggleMachine = (machineId) => {
    if (selectedMachines.includes(machineId)) {
      setSelectedMachines(selectedMachines.filter(id => id !== machineId));
      if (primaryMachine === machineId) {
        setPrimaryMachine('');
      }
    } else {
      setSelectedMachines([...selectedMachines, machineId]);
    }
  };

  // Build material options from actual data
  const materialOptions = [
    { value: '', label: 'Alle materialen' },
    ...(materials || []).filter(m => m).map(m => ({ value: m, label: m }))
  ];

  return (
    <div className="products-page">
      <div className="page-header d-flex justify-content-between align-items-center">
        <div>
          <h1>Producten</h1>
          <p className="text-muted">Beheer producten en machine koppelingen</p>
        </div>
        <Link to="/settings" className="btn btn-outline-primary">
          <i className="bi bi-arrow-repeat me-2"></i>
          Sync met Picqer
        </Link>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Zoeken</label>
              <div className="input-group">
                <span className="input-group-text">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Zoek op naam of code..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-4">
              <label className="form-label">Materiaal</label>
              <select
                className="form-select"
                value={filters.materialType}
                onChange={(e) => handleFilterChange('materialType', e.target.value)}
              >
                {materialOptions.map((mat) => (
                  <option key={mat.value} value={mat.value}>{mat.label}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <button
                className="btn btn-secondary w-100"
                onClick={() => setFilters({ materialType: '', search: '', page: 1 })}
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Products Table */}
      <div className="card">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Laden...</span>
              </div>
            </div>
          ) : !products || products.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-box fs-1 d-block mb-3"></i>
              <p>Geen producten gevonden</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Code</th>
                    <th>Materiaal</th>
                    <th>Primaire Machine</th>
                    <th>Compatibele Machines</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => (
                    <tr key={product._id}>
                      <td>
                        <Link to={`/products/${product._id}`} className="fw-medium">
                          {product.name}
                        </Link>
                      </td>
                      <td><code>{product.productCode}</code></td>
                      <td>
                        <span className="badge bg-light text-dark">
                          {product.materialType || '-'}
                        </span>
                      </td>
                      <td>
                        {product.primaryMachine ? (
                          <span className="badge bg-primary">
                            {product.primaryMachine.name || product.primaryMachine}
                          </span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        {product.compatibleMachines?.length > 0 ? (
                          <div className="d-flex flex-wrap gap-1">
                            {product.compatibleMachines.slice(0, 3).map((machine, index) => (
                              <span key={index} className="badge bg-secondary">
                                {machine.name || machine}
                              </span>
                            ))}
                            {product.compatibleMachines.length > 3 && (
                              <span className="badge bg-light text-dark">
                                +{product.compatibleMachines.length - 3}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => openMachineModal(product)}
                          title="Machines koppelen"
                        >
                          <i className="bi bi-gear"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="card-footer">
            <nav>
              <ul className="pagination justify-content-center mb-0">
                <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => setFilters({ ...filters, page: pagination.page - 1 })}
                  >
                    Vorige
                  </button>
                </li>
                <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => setFilters({ ...filters, page: pagination.page + 1 })}
                  >
                    Volgende
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>

      {/* Machine Assignment Modal */}
      {showMachineModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Machines koppelen - {selectedProduct?.name}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowMachineModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-4">
                  <label className="form-label fw-medium">Primaire Machine</label>
                  <p className="text-muted small">De machine die standaard gebruikt wordt voor dit product</p>
                  <select
                    className="form-select"
                    value={primaryMachine}
                    onChange={(e) => setPrimaryMachine(e.target.value)}
                  >
                    <option value="">Geen primaire machine</option>
                    {machines.filter(m => selectedMachines.includes(m._id)).map((machine) => (
                      <option key={machine._id} value={machine._id}>
                        {machine.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label fw-medium">Compatibele Machines</label>
                  <p className="text-muted small">Selecteer alle machines die dit product kunnen verwerken</p>
                  <div className="row">
                    {machines.map((machine) => (
                      <div key={machine._id} className="col-md-6 mb-2">
                        <div
                          className={`card cursor-pointer ${selectedMachines.includes(machine._id) ? 'border-primary' : ''}`}
                          onClick={() => toggleMachine(machine._id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="card-body py-2">
                            <div className="d-flex align-items-center">
                              <input
                                type="checkbox"
                                className="form-check-input me-3"
                                checked={selectedMachines.includes(machine._id)}
                                onChange={() => {}}
                              />
                              <div>
                                <div className="fw-medium">{machine.name}</div>
                                <small className="text-muted">{machine.type}</small>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowMachineModal(false)}
                >
                  Annuleren
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveMachines}
                >
                  Opslaan
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
